import type { TFunction } from 'i18next'
import { MenuItemTypes } from '@/constants'
import type { SideNavigationMenuGroup, SideNavigationMenuItem } from '@/ui/components/Header/SideNavigation/types/SideNavigationTypes'
import type { Space } from '@/shared/types/space'

/** One row of the skeleton: as long as the label of the item that it stands for, with a badge when the item has one */
export interface SideNavSkeletonItem {
    labelLength: number
    hasBadge: boolean
}

/** A list of the side navigation: `nested` is the group of items below a border (Feedback and Settings) */
export interface SideNavSkeletonGroup {
    /** The class of the list (`full-size` takes the free height, so that the next list is at the bottom) */
    customClasses: string
    items: SideNavSkeletonItem[]
    nested: boolean
}

const toItem = (item: SideNavigationMenuItem): SideNavSkeletonItem => ({
    labelLength: (item.label ?? '').length,
    hasBadge: (item.badges?.length ?? 0) > 0,
})

/**
 * The skeleton of an already built menu: the same lists, items and label lengths. A `group` item (the entries below a
 * border) becomes a nested list, a sublabel is left out (the skeleton has no headlines).
 */
export const skeletonGroupsFromMenu = (menuGroups: SideNavigationMenuGroup[] | null | undefined): SideNavSkeletonGroup[] =>
    (menuGroups ?? []).flatMap((group): SideNavSkeletonGroup[] => {
        const customClasses = group.customClasses ?? ''
        const entries = group.children ?? []
        const items = entries.filter(entry => entry.type === MenuItemTypes.item).map(toItem)
        const nestedGroups = entries
            .filter(entry => entry.type === MenuItemTypes.group)
            .map((nested): SideNavSkeletonGroup => ({
                customClasses,
                nested: true,
                items: (nested.children ?? []).filter(child => child.type === MenuItemTypes.item).map(toItem),
            }))

        return [{ customClasses, nested: false, items }, ...nestedGroups].filter(
            skeletonGroup => skeletonGroup.items.length > 0
        )
    })

/**
 * The skeleton of the menu of a space, from its configuration, before the menu itself is built: Home and the
 * assistants of the space in the first list, Feedback and Settings below (the same items as `Header.getMenuItems`).
 */
export const skeletonGroupsFromSpace = (
    space: Space | undefined,
    translate: TFunction,
    exists: (key: string) => boolean
): SideNavSkeletonGroup[] => {
    const assistants: SideNavSkeletonItem[] = (space?.assistants ?? []).map(assistant => {
        const { id, badges } = assistant.config
        const headerName = exists(`${id}:nameOnHeaderAndMenu`) ? translate(`${id}:nameOnHeaderAndMenu`) : ''

        return {
            labelLength: (headerName || translate(`${id}:name`)).length,
            hasBadge: (badges?.length ?? 0) > 0,
        }
    })

    return [
        {
            customClasses: 'full-size',
            nested: false,
            items: [{ labelLength: translate('general:sideNavigation:home').length, hasBadge: false }, ...assistants],
        },
        {
            customClasses: '',
            nested: true,
            items: [
                { labelLength: translate('feedback:title').length, hasBadge: false },
                { labelLength: translate('general:settings:title').length, hasBadge: false },
            ],
        },
    ]
}

/** What is shown when nothing is known about the menu yet */
export const defaultSkeletonGroups: SideNavSkeletonGroup[] = [
    { customClasses: 'full-size', nested: false, items: [8, 14, 12].map(labelLength => ({ labelLength, hasBadge: false })) },
    { customClasses: '', nested: true, items: [8, 8].map(labelLength => ({ labelLength, hasBadge: false })) },
]
